/**
 * KBC User Profile Component
 * Displays user information (name, email, photo) with KBC-themed styling
 * Can be used in different sizes: 'large' for landing page, 'small' for in-game
 */

import React, { useState, useEffect } from "react";
import { useMsal, useAccount } from "@azure/msal-react";
import { tokenRequest } from "../../authConfig";
import { callMsGraph } from "../../graph";
import { motion } from "framer-motion";

interface UserProfile {
  displayName: string;
  mail: string;
  jobTitle?: string;
  photo?: string;
}

interface KBCUserProfileProps {
  size?: 'large' | 'small';
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'inline';
  label?: string;
}

const KBCUserProfile: React.FC<KBCUserProfileProps> = ({ 
  size = 'large', 
  position = 'inline',
  label
}) => {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (account) {
      setLoading(true);
      instance
        .acquireTokenSilent({
          ...tokenRequest,
          account: account,
        })
        .then((response) => {
          if (response) {
            callMsGraph(response.accessToken)
              .then((result) => {
                setUserProfile(result);
                setLoading(false);
              })
              .catch((error) => {
                console.error("Error fetching user profile:", error);
                setLoading(false);
              });
          }
        })
        .catch((error) => {
          console.error("Error acquiring token:", error);
          setLoading(false);
        });
    }
  }, [account, instance]);

  if (!account) {
    return null;
  }

  // Position classes
  const positionClasses = {
    'top-right': 'absolute top-4 right-4 z-50',
    'top-left': 'absolute top-4 left-4 z-50',
    'bottom-right': 'absolute bottom-6 right-6 z-50',
    'bottom-left': 'absolute bottom-6 left-6 z-50',
    'inline': ''
  };

  // Large size - for landing page (prominent display)
  if (size === 'large') {
    return (
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={`${positionClasses[position]}`}
      >
        
        <div className="bg-white/10 backdrop-blur-lg border-2 border-maersk-blue/50 rounded-xl p-3 sm:p-4 shadow-2xl hover:shadow-maersk-blue/30 transition-all duration-300">
          {loading ? (
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-12 sm:h-12 bg-maersk-blue/20 rounded-full animate-pulse"></div>
              <div className="flex flex-col gap-2">
                <div className="w-24 sm:w-32 h-3 sm:h-4 bg-maersk-blue/20 rounded animate-pulse"></div>
                <div className="w-20 sm:w-24 h-2 sm:h-3 bg-maersk-blue/20 rounded animate-pulse"></div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Profile Picture */}
              {userProfile?.photo ? (
                <img
                  src={userProfile.photo}
                  alt={userProfile.displayName}
                  className="w-8 h-8 sm:w-12 sm:h-12 rounded-full object-cover border-2 sm:border-3 border-maersk-blue shadow-lg"
                />
              ) : (
                <div className="w-8 h-8 sm:w-12 sm:h-12 bg-gradient-to-br from-maersk-blue to-amber-600 rounded-full flex items-center justify-center text-maersk-navy font-bold text-sm sm:text-xl shadow-lg">
                  {(userProfile?.displayName || account.name)?.charAt(0).toUpperCase() || 'U'}
                </div>
              )}

              {/* User Details - Hide name on mobile, show icon instead */}
              <div className="hidden sm:flex flex-col">
                <span className="text-white font-bold text-lg leading-tight">
                  {userProfile?.displayName || account.name || 'Guest User'}
                </span>
                <span className="text-maersk-blue/80 text-sm leading-tight">
                  {userProfile?.mail || account.username || 'guest@example.com'}
                </span>
                {userProfile?.jobTitle && (
                  <span className="text-white/60 text-xs leading-tight mt-0.5">
                    {userProfile.jobTitle}
                  </span>
                )}
              </div>
              
              {/* User icon for mobile */}
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="18" 
                height="18" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                className="text-white sm:hidden"
              >
                <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </div>
          )}
        </div>
        {label && (
          <div className="text-white/90 text-xs sm:text-md font-semibold mb-2 text-center z-100">
            {label}
          </div>
        )}
      </motion.div>
    );
  }

  // Small size - for in-game screens (discrete)
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={`${positionClasses[position]} bg-white/5 backdrop-blur-sm border border-white/20 rounded-lg px-3 py-2 shadow-lg hover:bg-white/10 transition-all duration-200`}
    >
      {loading ? (
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-maersk-blue/20 rounded-full animate-pulse"></div>
          <div className="w-20 h-3 bg-maersk-blue/20 rounded animate-pulse"></div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          {/* Profile Picture - Small */}
          {userProfile?.photo ? (
            <img
              src={userProfile.photo}
              alt={userProfile.displayName}
              className="w-6 h-6 rounded-full object-cover border-2 border-maersk-blue/50"
            />
          ) : (
            <div className="w-6 h-6 bg-gradient-to-br from-maersk-blue to-amber-600 rounded-full flex items-center justify-center text-maersk-navy font-bold text-xs">
              {(userProfile?.displayName || account.name)?.charAt(0).toUpperCase() || 'U'}
            </div>
          )}

          {/* User Name - Small */}
          <span className="text-white text-sm font-medium">
            {userProfile?.displayName?.split(' ')[0] || account.name?.split(' ')[0] || 'Guest'}
          </span>
        </div>
      )}
    </motion.div>
  );
};

export default KBCUserProfile;
